import {
  WixPatternsContainer,
  WixPatternsContainerParams,
  CollectionState,
  FiltersMap,
  TaskState,
} from '@wix/bex-core';
import { InfiniteScrollTableState } from './InfiniteScrollTableState';
import { CardsGridState } from './CardsGridState';
import { runInAction, makeObservable, computed } from 'mobx';
import { ToolbarCollectionState } from './ToolbarCollectionState';
import { ViewTypeState } from './ViewTypeState';

export interface TableGridSwitchStateParams<T, F extends FiltersMap> {
  readonly collection: CollectionState<T, F>;
  readonly container: WixPatternsContainer;
  readonly containerOverrides: Partial<WixPatternsContainerParams>;
}
type ViewType = 'table' | 'grid';
const componentType = 'TableGridSwitch';
export class TableGridSwitchState<T, F extends FiltersMap> {
  collection: CollectionState<T, F>;
  readonly container;
  readonly containerOverrides: Partial<WixPatternsContainerParams>;
  readonly initTask = new TaskState();
  readonly gridState: CardsGridState<T, F>;
  readonly infiniteScrollTableState: InfiniteScrollTableState<T, F>;
  readonly viewTypeState: ViewTypeState;

  constructor(params: TableGridSwitchStateParams<T, F>) {
    this.container = params.container;
    this.containerOverrides = params.containerOverrides;

    this.collection = params.collection;

    this.infiniteScrollTableState = new InfiniteScrollTableState({
      collection: this.collection,
      container: this.container,
      toolbar: new ToolbarCollectionState({
        componentType,
        collection: this.collection,
        container: this.container,
        reportBi: (biParams) =>
          this.container.reportBi({
            ...biParams,
            params: {
              ...biParams.params,
              componentStatus: this.viewTypeState.viewType,
            },
          }),
      }),
    });

    this.gridState = new CardsGridState({
      collection: this.collection,
      container: this.container,
      toolbar: this.infiniteScrollTableState.toolbar,
    });

    this.viewTypeState = new ViewTypeState({
      container: this.container,
      dataCapsule: this.toolbar.dataCapsule,
      componentType,
      collection: this.collection,
    });

    makeObservable(this, {
      viewType: computed,
    });
  }

  get viewType() {
    return this.viewTypeState.viewType;
  }

  set viewType(viewType: ViewType) {
    runInAction(() => {
      this.viewTypeState.viewType = viewType;
    });
  }

  get toolbar() {
    return this.infiniteScrollTableState.toolbar;
  }

  init() {
    this.initTask.runOnce(async () => {
      await this.viewTypeState.init();
    });
  }
}
